import ReactAnd from 'react-and';
import { Button } from '../../../../../components/Clickables/Buttons/Button';
import { InternalLink } from '../../../../../components/Clickables/InternalLink/InternalLink';
import { IFAQData } from '../../../../../components/FAQ/FAQ';
import { getFishingWaterUrl, getSpeciesUrl } from '../../../../../helpers/urls';
import {
  GetFishingWater_fishingWater_region,
  GetRegion_entry_country,
  GetRegionTopWaters_entry_topFishingWaters_edges_node,
  GetSpeciesAreaRecordsList_species_edges_node,
} from '../../../../../interfaces/graphql';

interface IProps {
  country: GetRegion_entry_country | null;
  handleCTAClick: () => void;
  region: GetFishingWater_fishingWater_region | null;
  topSpecies?: GetSpeciesAreaRecordsList_species_edges_node[];
  topFishingWaters?: GetRegionTopWaters_entry_topFishingWaters_edges_node[];
  hasRegulations?: boolean;
}

const NUMBER_OF_FISHING_WATERS_TO_SHOW = 3;
const NUMBER_OF_FISHING_SPECIES_TO_SHOW = 3;

export const getFAQData = ({
  country,
  handleCTAClick,
  region,
  topSpecies,
  topFishingWaters,
  hasRegulations,
}: IProps): IFAQData[] => {
  const countryName = country?.countryName;
  const regionName = region?.name ?? undefined;

  return [
    ...(hasRegulations
      ? [
          {
            id: '1',
            title: `📜 What are the fishing regulations in ${regionName ? `${regionName}, ` : ''}${countryName}?`,
            body: (
              <>
                {`Fishing regulations in ${regionName ? `${regionName}, ` : ''}${countryName} vary by location and species, covering size limits, bag limits, and open seasons. To stay up to date and avoid guesswork, use the `}
                <Button
                  onClick={handleCTAClick}
                  size="sm"
                  className="body-text-sm text-primary font-semibold"
                  variant="textNone"
                >
                  Fishbrain app
                </Button>
                {` for the latest rules on every fish and body of water.`}
              </>
            ),
            bodyString: `Fishing regulations in ${regionName ? `${regionName}, ` : ''}${countryName} vary by location and species, covering size limits, bag limits, and open seasons. To stay up to date and avoid guesswork, use the Fishbrain app for the latest rules on every fish and body of water.`,
          },
        ]
      : []),
    ...(topSpecies && topSpecies.length > 0
      ? [
          {
            id: '2',
            title: `🐟 What are the best fish species to catch in ${regionName ? `${regionName}, ` : ''}${countryName}?`,
            body: (
              <>
                {`Anglers in ${regionName ? `${regionName}, ` : ''}${countryName} most often target `}
                <ReactAnd>
                  {topSpecies
                    .slice(0, NUMBER_OF_FISHING_SPECIES_TO_SHOW)
                    .map(({ displayName, _id }) => {
                      return (
                        <InternalLink
                          key={`species-${_id}`}
                          to={getSpeciesUrl(_id, displayName)}
                          className="body-text-sm text-primary"
                        >
                          {displayName}
                        </InternalLink>
                      );
                    })}
                </ReactAnd>
                {`. For more detailed insights, the `}
                <Button
                  onClick={handleCTAClick}
                  size="sm"
                  className="body-text-sm text-primary font-semibold"
                  variant="textNone"
                >
                  Fishbrain app
                </Button>
                {` shows exactly which species are biting in each lake, river, and coastline.`}
              </>
            ),
            bodyString: `Anglers in ${regionName ? `${regionName}, ` : ''}${countryName} most often target ${topSpecies
              .slice(0, NUMBER_OF_FISHING_SPECIES_TO_SHOW)
              .map(
                ({ _id, displayName }) =>
                  `<a href="${getSpeciesUrl(_id, displayName)}">${displayName}</a>`,
              )
              .join(
                ', ',
              )}. For more detailed insights, the Fishbrain app shows exactly which species are biting in each lake, river, and coastline.`,
          },
        ]
      : []),
    ...(topFishingWaters && topFishingWaters.length > 0
      ? [
          {
            id: '3',
            title: `🌊 Where are the top fishing spots in ${regionName ? `${regionName}, ` : ''}${countryName}?`,
            body: (
              <>
                {`Popular waters in ${regionName ? `${regionName}, ` : ''}${countryName} include `}
                <ReactAnd>
                  {topFishingWaters
                    .slice(0, NUMBER_OF_FISHING_WATERS_TO_SHOW)
                    .flatMap(({ displayName, _id }) => {
                      return (
                        <InternalLink
                          key={`fishing-water-${_id}`}
                          to={getFishingWaterUrl(_id, displayName)}
                          className="body-text-sm text-primary"
                        >
                          {displayName}
                        </InternalLink>
                      );
                    })}
                </ReactAnd>
                {`. With the `}
                <Button
                  onClick={handleCTAClick}
                  size="sm"
                  className="body-text-sm text-primary font-semibold"
                  variant="textNone"
                >
                  Fishbrain app
                </Button>
                {`, you can zoom in on any area to discover the best spots and see what other anglers are catching there.`}
              </>
            ),
            bodyString: `Popular waters in ${regionName ? `${regionName}, ` : ''}${countryName} include ${topFishingWaters
              .slice(0, NUMBER_OF_FISHING_WATERS_TO_SHOW)
              .flatMap(({ displayName, _id }) => {
                return `<a href="${getFishingWaterUrl(_id, displayName)}">${displayName}</a>`;
              })
              .join(
                ', ',
              )}. With the Fishbrain app, you can zoom in on any area to discover the best spots and see what other anglers are catching there.`,
          },
        ]
      : []),
  ];
};
